<script lang="ts" setup>
import { ref } from "vue";

// api
import { userApi } from "@/api/user";

// components
import { Dropdown as VDropdown } from "floating-vue";
import { GButton, GLoader, GSimpleErrorView } from "@gwrk/g-components";

// composables
import { createMachine } from "xstate";
import { useMachine } from "@xstate/vue";
import { useI18n } from "vue-i18n";
import { useCompanyId } from "@/stores/useCompanyId";

// interfaces
import type { AllCompaniesResponse } from "@/interfaces/User";

// enums
import {
  ButtonTextAlignment,
  ButtonVariant,
  ButtonWidth,
  IconPlacement,
  Size,
} from "@gwrk/g-components/enums";
import { Route } from "@/enums/Route";
import { Icon } from "@/enums/Icon";

const { t } = useI18n();
const companyIdStore = useCompanyId();

const xstates = {
  LOADER_VISIBLE: "loaderVisible",
  ALL_COMPANIES_VISIBLE: "allCompaniesVisible",
  ERROR_PAGE_VISIBLE: "errorPageVisible",
};

const allCompaniesMachine = createMachine({
  id: "all-companies-machine",
  initial: xstates.LOADER_VISIBLE,
  states: {
    loaderVisible: {
      on: {
        SET_ALL_COMPANIES: xstates.ALL_COMPANIES_VISIBLE,
        SET_ERROR_PAGE: xstates.ERROR_PAGE_VISIBLE,
        SET_LOADER: xstates.LOADER_VISIBLE,
      },
    },
    allCompaniesVisible: {
      on: { SET_LOADER: xstates.LOADER_VISIBLE },
    },
    errorPageVisible: {
      on: { SET_LOADER: xstates.LOADER_VISIBLE },
    },
  },
});

const { state: xstate, send } = useMachine(allCompaniesMachine);

const allCompanies = ref<AllCompaniesResponse[]>([]);

const fetchAllCompanies = async () => {
  try {
    allCompanies.value = await userApi().getAllCompanies({
      companyId: companyIdStore.companyId,
    });
    send("SET_ALL_COMPANIES");
  } catch (error) {
    console.error(error);
    send("SET_ERROR_PAGE");
  }
};
</script>

<template>
  <div class="g-account-controls">
    <VDropdown class="w-fit">
      <GButton
        :button-variant="ButtonVariant.TRANSPARENT"
        :icon="Icon.CHEVRON_DOWN_SOLID"
        :icon-placement="IconPlacement.AFTER"
        :icon-size="Size.XS"
        :text-alignment="ButtonTextAlignment.LEFT"
        :padding="true"
        @click="fetchAllCompanies"
      >
        <span class="text-base">{{
          t("job_offers_listing.change_company")
        }}</span>
      </GButton>
      <template #popper>
        <div class="flex flex-col items-center gap-2 max-h-96">
          <GLoader v-if="xstate.value === xstates.LOADER_VISIBLE" />
          <GSimpleErrorView
            v-if="xstate.value === xstates.ERROR_PAGE_VISIBLE"
          />
          <template v-if="xstate.value === xstates.ALL_COMPANIES_VISIBLE">
            <GButton
              v-for="{ name, city, companyId } in allCompanies"
              :key="`company-selection-${companyId}`"
              :button-variant="ButtonVariant.TRANSPARENT"
              :class="{
                'font-bold': companyId.toString() === companyIdStore.companyId,
              }"
              :width="ButtonWidth.FULL"
              :href="{
                name: Route.JOB_OFFERS_LISTING_ACTIVE,
                params: { companyId },
              }"
            >
              {{ name }}, {{ city }}
            </GButton>
          </template>
        </div>
      </template>
    </VDropdown>
  </div>
</template>

<style lang="scss" scoped>
.g-account-controls {
}
</style>
